import React, { useState } from 'react';
import { useOrganizationEnums } from '../../hooks/useOrganizationEnums';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import ProjectCard from './ProjectCard';
import ProjectModal from './modals/ProjectModal';
import DeleteProjectDialog from './modals/DeleteProjectDialog';
import TaskModal from './modals/TaskModal';
import { FaTasks, FaCircle, FaCheckCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import {
  useProjects,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
} from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';
import { useUsers } from '../../hooks/useUsers';
import { useCreateTask, useTasks } from '../../hooks/useTasks';
import { CreateProjectData, UpdateProjectData, Project } from '../../api/projectApi';
import { CreateTaskData } from '../../api/taskApi';
import { useAuthenticatedUser } from '../../contexts/AuthenticatedUserInfoContext';
import { PermissionGuard } from '../PermissionGuard';
import { Permission } from '../../utils/permissions';
import LimitRestrictedButton from '../LimitRestrictedButton';

const ProjectManagement: React.FC = () => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const { authenticatedUser } = useAuthenticatedUser();
  const navigate = useNavigate();
  // Toggle for show all projects (admin/editor only)
  const [showAllProjects, setShowAllProjects] = useState(false);
  const canViewAll = !!authenticatedUser && (authenticatedUser.role === 'admin' || authenticatedUser.role === 'editor');
  // State to show/hide archived projects
  const [showArchived, setShowArchived] = useState(false);
  const { data: projects, isLoading, isError, error } = useProjects(Boolean(showAllProjects && canViewAll), showArchived);
  const { data: clients } = useClients();
  const { data: users } = useUsers();
  const { data: tasks } = useTasks();
  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();
  const deleteProjectMutation = useDeleteProject();
  const createTaskMutation = useCreateTask();

  const cardBg = useColorModeValue('white', 'gray.50');
  const borderColor = useColorModeValue('gray.200', 'gray.300');
  const expandButtonBg = useColorModeValue('gray.50', 'gray.700');
  const taskContainerBg = useColorModeValue('gray.50', 'gray.700');
  const taskItemBg = useColorModeValue('white', 'gray.600');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const {
    isOpen: isTaskModalOpen,
    onOpen: onTaskModalOpen,
    onClose: onTaskModalClose,
  } = useDisclosure();

  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [selectedProjectForTask, setSelectedProjectForTask] = useState<Project | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<CreateProjectData>({
    userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
    projectName: '',
    type: 'software',
    client: '',
  });

  const [taskFormData, setTaskFormData] = useState<CreateTaskData>({
    taskTitle: '',
    priority: 'medium',
    description: '',
    type: 'development',
    project: '',
    status: 'Not Started',
    authBy: [],
    quotedPrice: undefined,
    dateStarted: undefined,
    dueDate: undefined,
  });

  // State for tasks being created with the project
  const [projectTasks, setProjectTasks] = useState<Partial<CreateTaskData>[]>([]);
  const [showTaskCreation, setShowTaskCreation] = useState(false);

  const toast = useToast();
  const cancelRef = React.useRef<HTMLButtonElement>(null!);
  const { data: organizationEnums } = useOrganizationEnums();
  const projectTypes = organizationEnums?.projectTypes ?? [];

  const handleCreateProject = async (projectData: CreateProjectData, tasks: Partial<CreateTaskData>[]) => {
    if (!projectData.projectName.trim()) {
      toast({
        title: 'Error',
        description: 'Project name is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      // Normalize authorizedBy to array of string IDs
      const authorizedBy = Array.isArray(projectData.authorizedBy)
        ? projectData.authorizedBy.map((u: any) => typeof u === 'string' ? u : (u && typeof u === 'object' && '_id' in u ? (u as { _id: string })._id : ''))
        : [];
      // Create the project first, including all fields from formData
      const newProject = await createProjectMutation.mutateAsync({
        ...projectData,
        userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
        authorizedBy,
      });
      // If there are tasks to create, create them and add to the project
      if (tasks.length > 0) {
        const taskIds: string[] = [];
        for (const taskData of tasks) {
          if (taskData.description) {
            const task = await createTaskMutation.mutateAsync({
              taskTitle: taskData.taskTitle || '',
              priority: taskData.priority || 'medium',
              description: taskData.description,
              type: taskData.type || 'development',
              status: taskData.status || 'In progress',
              authBy: Array.isArray(taskData.authBy) ? taskData.authBy : [],
              quotedPrice: taskData.quotedPrice,
              dateStarted: taskData.dateStarted,
              dueDate: taskData.dueDate,
              assignedToUser: taskData.assignedToUser,
              project: newProject._id, // <-- ensure project is set
            }) as import('../../api/taskApi').Task;
            taskIds.push(task._id);
          }
        }
        // Update the project with the new tasks if any were created
        if (taskIds.length > 0) {
        await updateProjectMutation.mutateAsync({
          id: newProject._id,
          data: {
            ...projectData,
            ...newProject,
            tasks: taskIds,
          } as UpdateProjectData,
        });
        }
      }
      toast({
        title: 'Success',
        description: `Project created successfully${tasks.length > 0 ? ` with ${tasks.length} task(s)` : ''}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setFormData({
        userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
        projectName: '',
        type: 'software',
        client: '',
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create project',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateProject = async () => {
    if (!editingProject || !formData.projectName.trim()) {
      toast({
        title: 'Error',
        description: 'Project name is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await updateProjectMutation.mutateAsync({
        id: editingProject._id,
        data: {
          projectName: formData.projectName,
          type: formData.type,
        } as UpdateProjectData,
      });
      toast({
        title: 'Success',
        description: 'Project updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setEditingProject(null);
      setFormData({
        userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
        projectName: '',
        type: 'software',
        client: '',
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update project',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteProject = async () => {
    if (!deletingProject) return;

    try {
      await deleteProjectMutation.mutateAsync(deletingProject._id);
      toast({
        title: 'Success',
        description: 'Project deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setDeletingProject(null);
      onDeleteClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete project',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateTask = async () => {
    if (
      !taskFormData.description ||
      typeof taskFormData.description !== 'string' ||
      taskFormData.description.trim() === '' ||
      !selectedProjectForTask
    ) {
      toast({
        title: 'Error',
        description: 'Description and project are required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Create the task
      const newTask = await createTaskMutation.mutateAsync({
        ...taskFormData,
        project: selectedProjectForTask._id,
        assignedToUser: taskFormData.assignedToUser,
      });

      // Update the project to include the new task
      const updatedTaskIds = [...selectedProjectForTask.tasks, newTask._id];

      await updateProjectMutation.mutateAsync({
        id: selectedProjectForTask._id,
        data: {
          projectName: selectedProjectForTask.projectName,
          type: selectedProjectForTask.type,
          tasks: updatedTaskIds,
        } as UpdateProjectData,
      });

      toast({
        title: 'Success',
        description: 'Task created and added to project successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset form and close modal
      setTaskFormData({
        taskTitle: '',
        priority: 'medium',
        description: '',
        type: 'development',
        project: '',
        status: 'In progress',
        authBy: [],
        quotedPrice: undefined,
        dateStarted: undefined,
        dueDate: undefined,
      });
      setSelectedProjectForTask(null);
      onTaskModalClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create task',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle user selection for authBy in task creation
  const handleUserToggle = (userId: string) => {
    const currentAuthBy = taskFormData.authBy || [];
    const isSelected = currentAuthBy.includes(userId);

    if (isSelected) {
      // Remove user
      setTaskFormData({
        ...taskFormData,
        authBy: currentAuthBy.filter(id => id !== userId),
      });
    } else {
      // Add user
      setTaskFormData({
        ...taskFormData,
        authBy: [...currentAuthBy, userId],
      });
    }
  };

  const getSelectedUserNames = () => {
    if (!taskFormData.authBy || taskFormData.authBy.length === 0) return [];
    return taskFormData.authBy.map(userId => {
      const user = users?.find(u => u._id === userId);
      return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
    });
  };

  const addTaskToProject = () => {
    setProjectTasks([
      ...projectTasks,
      {
        priority: 'medium',
        description: '',
        type: 'development',
        status: 'In progress',
        authBy: [],
        quotedPrice: undefined,
        dateStarted: undefined,
        dueDate: undefined,
      },
    ]);
  };

  const removeTaskFromProject = (index: number) => {
    setProjectTasks(projectTasks.filter((_, i) => i !== index));
  };

  const updateProjectTask = (index: number, taskData: Partial<CreateTaskData>) => {
    const updatedTasks = [...projectTasks];
    updatedTasks[index] = { ...updatedTasks[index], ...taskData };
    setProjectTasks(updatedTasks);
  };

  const openCreateModal = () => {
    setEditingProject(null);
    setFormData({
      userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
      projectName: '',
      type: 'software',
      client: '',
    });
    setProjectTasks([]);
    setShowTaskCreation(false);
    onOpen();
  };

  const openDeleteDialog = (project: Project) => {
    setDeletingProject(project);
    onDeleteOpen();
  };

  const openTaskModal = (project: Project) => {
    setSelectedProjectForTask(project);
    setTaskFormData({
      taskTitle: '',
      priority: 'medium',
      description: '',
      type: 'development',
      project: project._id,
      status: 'In progress',
      authBy: [],
      quotedPrice: undefined,
      dateStarted: undefined,
      dueDate: undefined,
    });
    onTaskModalOpen();
  };

  const getClientName = (clientData: Project['client']) => {
    if (!clientData) return 'Unknown Client';
    if (typeof clientData === 'string') {
      const client = clients?.find(c => c._id === clientData);
      return client?.clientName || 'Unknown Client';
    }
    return clientData.clientName || 'Unknown Client';
  };

  // Get tasks for a specific project
  const getProjectTasks = (projectId: string) => {
    if (!tasks) return [];
    return tasks.filter(task => {
      if (!task.project) return false;
      const taskProjectId = typeof task.project === 'string' ? task.project : task.project._id;
      return taskProjectId === projectId;
    });
  };

  // Toggle project expansion
  const toggleProjectExpansion = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  // Get status color for badges
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'not started':
        return 'gray';
      case 'completed':
        return 'green';
      case 'in progress':
        return 'blue';
      case 'pending':
        return 'orange';
      case 'on hold':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Get priority color for badges
  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return FaCheckCircle;
      case 'in progress':
        return FaClock;
      case 'pending':
        return FaCircle;
      case 'on hold':
        return FaExclamationTriangle;
      default:
        return FaCircle;
    }
  };

  // Helper function to format date for HTML date input
  const formatDateForInput = (date: Date | string | undefined | null): string => {
    if (!date) return '';
    if (typeof date === 'string') return date;
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };


  // Instant search: if searchQuery is empty, return all projects immediately (no debounce, no memoization)
  let filteredProjects = !searchQuery.trim()
    ? (projects || [])
    : (projects || []).filter(p =>
        (p.projectName && p.projectName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.projectNumber && p.projectNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (typeof p.client === 'object' && p.client?.clientName && p.client.clientName.toLowerCase().includes(searchQuery.toLowerCase()))
      );

  // Filter archived projects if needed
  if (!showArchived) {
    filteredProjects = filteredProjects.filter(p => !p.archived);
  }

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Loading projects...</Text>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={8} textAlign="center">
        <Text color="red.500">Error loading projects: {error?.message || 'Unknown error'}</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Project Management</Heading>
        <HStack spacing={3}>
          <Button
            size="md"
            colorScheme={showArchived ? 'gray' : 'orange'}
            variant={showArchived ? 'solid' : 'outline'}
            onClick={() => setShowArchived(v => !v)}
          >
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
          {canViewAll && (
            <Button
              size="md"
              colorScheme={showAllProjects ? 'blue' : 'green'}
              variant={showAllProjects ? 'solid' : 'outline'}
              onClick={() => setShowAllProjects(v => !v)}
            >
              {showAllProjects ? 'Show Only My Projects' : 'Show All Other Projects'}
            </Button>
          )}
          <PermissionGuard permission={Permission.CREATE_PROJECTS}>
            <LimitRestrictedButton resource="projects" leftIcon={<AddIcon />} colorScheme="green" onClick={openCreateModal}>
              Add Project
            </LimitRestrictedButton>
          </PermissionGuard>
        </HStack>
      </Flex>

      {/* Search Bar */}
      <Box mb={4} maxW="100%" bg={cardBg} p={4} borderRadius="md" boxShadow="sm">
        <Input
          variant="outline"
          size="md"
          placeholder="Search projects by name, number, description, or client..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </Box>

      {/* Always show project grid, no overview */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {filteredProjects.map(project => {
          const projectTasks = getProjectTasks(project._id);
          const isExpanded = expandedProjects.has(project._id);
          const completedTasks = projectTasks.filter(task => task.status === 'Completed').length;
          const urgentTasks = projectTasks.filter(task => task.priority === 'urgent').length;
          return (
            <ProjectCard
              key={project._id}
              project={project}
              projectTasks={projectTasks}
              isExpanded={isExpanded}
              completedTasks={completedTasks}
              urgentTasks={urgentTasks}
              onEdit={async (updatedProjectData) => {
                if (!updatedProjectData || !project._id) return;
                try {
                  await updateProjectMutation.mutateAsync({
                    id: project._id,
                    data: {
                      projectName: updatedProjectData.projectName,
                      type: updatedProjectData.type,
                      spec: updatedProjectData.spec,
                      tasks: updatedProjectData.tasks,
                      description: updatedProjectData.description,
                      authorizedBy: updatedProjectData.authorizedBy,
                      quotedPrice: updatedProjectData.quotedPrice,
                      agreedPrice: updatedProjectData.agreedPrice,
                      toBeginOn: updatedProjectData.toBeginOn,
                      toBeCompletedBy: updatedProjectData.toBeCompletedBy,
                    },
                  });
                } catch (err) {
                  // Optionally handle error
                }
              }}
              onDelete={() => {
                openDeleteDialog(project);
              }}
              onView={() => {
                navigate(`/dashboard/project/${project._id}/details`);
              }}
              onToggleExpand={() => toggleProjectExpansion(project._id)}
              onAddTask={() => {
                openTaskModal(project);
              }}
              getStatusIcon={getStatusIcon}
              getStatusColor={getStatusColor}
              getPriorityColor={getPriorityColor}
              borderColor={borderColor}
              expandButtonBg={expandButtonBg}
              taskContainerBg={taskContainerBg}
              taskItemBg={taskItemBg}
              getClientName={getClientName}
              clients={clients || []}
            />
          );
        })}
      </SimpleGrid>

      {filteredProjects.length === 0 && (
        <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" py={10} bg={cardBg} borderRadius="md" boxShadow="sm" minH={'50vh'}>
          <Text fontSize="lg" color="gray.500" fontWeight={'semibold'}>
            No projects found. 
          </Text>    
          <Text fontSize="lg" color="gray.500">
            Create your first project to get started!
          </Text>

            <HStack spacing={6} justifyContent="center" mt={4}>
              <PermissionGuard permission={Permission.CREATE_PROJECTS}>
                <LimitRestrictedButton resource="projects" leftIcon={<AddIcon />} colorScheme="green" onClick={openCreateModal}>
                  Add Project
                </LimitRestrictedButton>
              </PermissionGuard>

              {canViewAll && (
                <Button
                  size="md"
                  colorScheme={showAllProjects ? 'blue' : 'blue'}
                  variant={showAllProjects ? 'solid' : 'solid'}
                  onClick={() => setShowAllProjects(v => !v)}
                >
                  {showAllProjects ? 'Show Only My Projects' : 'Show All Projects'}
                </Button>
              )}
          </HStack>
        </Box>
      )}

      {/* Create/Edit Modal */}
      <ProjectModal
        isOpen={isOpen}
        onClose={onClose}
        onSave={editingProject ? handleUpdateProject : handleCreateProject}
        isLoading={editingProject ? updateProjectMutation.isPending : createProjectMutation.isPending}
        editingProject={editingProject}
        formData={formData}
        setFormData={setFormData}
        projectTypes={projectTypes}
        clients={clients || []}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteProjectDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        onDelete={handleDeleteProject}
        isLoading={deleteProjectMutation.isPending}
        projectName={deletingProject?.projectName}
      />

      {/* Task Creation Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={onTaskModalClose}
        onSave={handleCreateTask}
        isLoading={createTaskMutation.isPending}
        projectName={selectedProjectForTask?.projectName}
        taskFormData={taskFormData}
        setTaskFormData={setTaskFormData}
        users={users || []}
        getSelectedUserNames={getSelectedUserNames}
        handleUserToggle={handleUserToggle}
        formatDateForInput={formatDateForInput}
      />
    </Box>
  );
};

export default ProjectManagement;
